import React, { useState, useEffect, useCallback } from 'react';
import { List, ListItem, Divider, Drawer, Icon } from '@material-ui/core';
import { get, map, size } from 'global/lib/lodash';
import useStyles from './styles';
import Skeleton from '@material-ui/lab/Skeleton';
import { useScreen } from 'global/styles';
import { useLocation } from 'react-router-dom';
import { getId } from './index';
import clsx from 'clsx';

const SideBarList = (props) => {
  const { isCurrent, system, setSidebarOpen, hash, setHash, setActiveEdit, activeEdit, canShowScores } = props;
  const [open, setOpen] = useState(isCurrent);
  const classes = useStyles();

  const toggleOpen = (bool) => {
    setOpen(bool);
  };

  useEffect(() => {
    toggleOpen(isCurrent);
  }, [isCurrent]);

  const onClick = useCallback((checklist) => {
    setHash(getId(checklist));
    if (activeEdit && getId(get(activeEdit, 'checklist')) !== getId(checklist)) {
      setActiveEdit({
        checklist,
        system,
        scroll: true,
        scrollBy: 48,
      });
    }
  });

  return (
    <>
      <ListItem button className={isCurrent ? classes.systemActive : classes.systemInactive} onClick={() => toggleOpen(!open)}>
        <div className={classes.listItemWrapper}>
          <span className={classes.headerChild}>{system.name}</span>
          {canShowScores && <span className={clsx(classes.headerChild, classes.sidebarHeaderChild)}>{system.displayScore}</span>}
          <Icon className={classes.expandIcon}>{open ? 'expand_less' : 'expand_more'}</Icon>
        </div>
      </ListItem>
      <div className={open ? classes.show : classes.hide} onClick={() => setSidebarOpen(false)}>
        {get(props, 'checklists', []).map((checklist) => {
          const id = getId(checklist).toString();
          return (
            <ListItem
              key={id}
              button={true}
              onClick={() => onClick(checklist)}
              className={clsx(classes.listItemWrapper, classes.listItemWrapChild, hash === id ? classes.checklistActive : classes.checklistInactive)}
            >
              <span className={classes.checklistMenuHeader}>{checklist.name}</span>
              {canShowScores && <span className={classes.checklistMenuHeader}>{checklist.displayScore}</span>}
            </ListItem>
          );
        })}
      </div>
      <Divider />
    </>
  );
};

export default (props) => {
  const { assessment, systems, loading, open, setSidebarOpen, setActiveEdit, activeEdit, canShowScores, scorecardIndex } = props;
  const location = useLocation();
  const { isMobile } = useScreen();
  const classes = useStyles();
  const [hash, setHash] = useState();

  const DRAW = isMobile ? Drawer : 'div';

  useEffect(() => {
    // This triggers whenever a user actively changes something in the interface,
    // whether it's clicking "Edit" or it's clicking on a sidebar to navigate
    // (If a user is just scrolling, this if statement won't ever evaluate to true.)
    if (hash && hash !== get(location, 'state.id', '')) {
      location.state = { id: hash };
      if (!activeEdit) {
        // triggers when a user clicks on the sidebar menu item while not in Edit mode
        document.getElementById(hash).scrollIntoView(true);
        window.scrollBy(0, -48);
      }
    }
  }, [hash]);

  useEffect(() => {
    try {
      if (activeEdit) {
        // triggers when user enters Edit Mode
        setHash(getId(get(activeEdit, 'checklist')));
      } else if (hash) {
        // triggers when user exits Edit Mode: scroll to the item just edited
        document.getElementById(hash).scrollIntoView(true);
        window.scrollBy(0, -48);
      }
    } catch (err) {
      console.error(err);
    }
  }, [activeEdit]);

  const handleScroll = () => {
    setHash(get(location, 'state.id', ''));
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const renderScore = () => {
    const data = get(assessment, 'data');
    const scores = get(data, 'scores');
    const scardId = get(data, ['scorecards', scorecardIndex, 'id']);

    return `Overall: ${get(scores, [scardId, 'displayScore'])}`;
  };

  return (
    <DRAW className={isMobile ? classes.leftSidebarMobile : classes.leftSidebar} open={open} onClose={() => setSidebarOpen(false)}>
      {(loading || !size(systems)) && <Skeleton variant="rect" height={2000} />}
      <List>
        <ListItem className={classes.overallScore}>{renderScore()}</ListItem>
        {map(systems, (system, i) => {
          const isCurrent = hash && (getId(system).toString() === hash || get(system, 'children', []).find((ps) => getId(ps).toString() === hash));
          return (
            <SideBarList
              key={i}
              system={system}
              checklists={system.children}
              setSidebarOpen={setSidebarOpen}
              isCurrent={isCurrent}
              hash={hash}
              setHash={setHash}
              setActiveEdit={setActiveEdit}
              activeEdit={activeEdit}
              canShowScores={canShowScores}
            />
          );
        })}
      </List>
    </DRAW>
  );
};
